import classNames from "classnames";
import { openDownloadUrl } from "util/openDownloadUrl";
import Loader from "components/DesignSystem/Loader/loader";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import Tab from "components/DesignSystem/Tab/Tab";
import Table from "components/DesignSystem/Table/V2/Table";
import { DownloadIcon } from "components/icons/Download";
import { PricedTicket } from "components/icons/PricedTicket";
import { UnpricedTicket } from "components/icons/UnpricedTicket";
import { EmptyList } from "components/Illustrations/EmptyList";
import { TableEmptyState } from "components/TableEmptyState/TableEmptyState";
import { INKLE_TEAM_AND_CPA_TEAM } from "constants/chatType";
import {
  TICKET_CREATE_END_DATE,
  TICKET_CREATE_SELECT_PERIOD,
  TICKET_CREATE_START_DATE,
  TICKET_ETD_END_DATE,
  TICKET_ETD_SELECT_PERIOD,
  TICKET_ETD_START_DATE,
  TICKET_SORT_OPTIONS,
  TICKET_RESOLVED_END_DATE,
  TICKET_RESOLVED_SELECT_PERIOD,
  TICKET_RESOLVED_START_DATE,
} from "constants/ticket";
import { useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { flexRender, getCoreRowModel, useReactTable } from "react-table-8.10.7";
import { useGetAssignAgentsQuery } from "store/apis/agents";
import { useGetTagsForCategoryQuery } from "store/apis/billing";
import {
  useGetChatTicketsQuery,
  useLazyGetChatTicketReportsQuery,
} from "store/apis/chatTicket";
import { useGetServiceTeamQuery } from "store/apis/serviceTeam";
import { useChatContext } from "stream-chat-react";
import { ChatTicket, TicketNotes } from "types/Models/chatTicket";
import { BackendError } from "types/utils/error";
import { debounce } from "utils/debouncing";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { CreateTicket } from "./CreateTicket";
import { ticketColumns } from "./TicketCells";
import { TicketFilter } from "./TicketFilter";
import { TicketSlider } from "./TicketSlider";
import Wind from "components/icons/Wind";

const EMPTY_TICKETS: ChatTicket[] = [];
const EMPTY_CALL_AGENTS: { uuid: string; name: string }[] = [];
const EMPTY_ASSIGNEES: { uuid: string; name: string }[] = [];
const EMPTY_TAGS: { uuid: string; title: string }[] = [];

const renderTicketTypeIcon = (isPricedTicket: boolean) =>
  isPricedTicket ? <PricedTicket /> : <UnpricedTicket />;

type TicketFilterValues = {
  page: number;
  ticketUuid: string;
  tag_filter: string | null;
  assignee_profile_ids: string | null;
  agent_ids: string | null;
  created_by_profile_ids: string | null;
  ticket_type: string | null;
  ticket_etd: string | null;
  priority: string | null;
  task_name: string | null;
  sort_by: string | null;
  [TICKET_ETD_START_DATE]: string | null;
  [TICKET_ETD_END_DATE]: string | null;
  [TICKET_ETD_SELECT_PERIOD]: string | null;
  [TICKET_CREATE_START_DATE]: string | null;
  [TICKET_CREATE_END_DATE]: string | null;
  [TICKET_CREATE_SELECT_PERIOD]: string | null;
  [TICKET_RESOLVED_START_DATE]: string | null;
  [TICKET_RESOLVED_END_DATE]: string | null;
  [TICKET_RESOLVED_SELECT_PERIOD]: string | null;
};

const INITIAL_TICKET_FILTER_VALUES: TicketFilterValues = {
  page: 1,
  ticketUuid: "",
  tag_filter: null,
  assignee_profile_ids: null,
  agent_ids: null,
  created_by_profile_ids: null,
  ticket_type: null,
  ticket_etd: null,
  priority: null,
  task_name: null,
  sort_by: null,
  [TICKET_ETD_START_DATE]: null,
  [TICKET_ETD_END_DATE]: null,
  [TICKET_ETD_SELECT_PERIOD]: null,
  [TICKET_CREATE_START_DATE]: null,
  [TICKET_CREATE_END_DATE]: null,
  [TICKET_CREATE_SELECT_PERIOD]: null,
  [TICKET_RESOLVED_START_DATE]: null,
  [TICKET_RESOLVED_END_DATE]: null,
  [TICKET_RESOLVED_SELECT_PERIOD]: null,
};

export const Ticket = ({
  companyGroupUUID = "",
}: {
  companyGroupUUID?: string;
}) => {
  usePageTitle("Support tickets");
  const { isAdmin, isCustomer, canManageInternalTickets } = useRoleBasedView();
  const [resolvedStatus, setResolvedStatus] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const { values, updateFilter, updateMultipleFilters } =
    useFilters<TicketFilterValues>({
      initialValue: INITIAL_TICKET_FILTER_VALUES,
      useQueryParams: true,
    });

  const selectedTag = values.tag_filter || null;
  const selectedAssignee = values.assignee_profile_ids || null;
  const selectedAgent = values.agent_ids || null;
  const selectedCreatedBy = values.created_by_profile_ids || null;
  const selectedTicketType = values.ticket_type || null;
  const selectedTicketETD = values.ticket_etd || null;
  const pageNumber = Number(values.page || 1);
  const ticketIdFromQueryParam = values.ticketUuid || "";
  const [isTabSwitched, setIsTabSwitched] = useState(false);
  const [ticketNotesPage, setTicketNotesPage] = useState(1);
  const [ticketNotes, setTicketNotes] = useState<TicketNotes[]>([]);
  const { client: chatClient } = useChatContext();
  const [openSlider, setOpenSlider] = useState(Boolean(ticketIdFromQueryParam));
  const isInsideGroup = Boolean(companyGroupUUID);
  const { data: callAgents = EMPTY_CALL_AGENTS } = useGetAssignAgentsQuery(
    {
      agentType: "CALL_EXECUTIVE",
    },
    { skip: !isAdmin }
  );
  const selectedPriority = values.priority || null;
  const selectedETDStartDate = values[TICKET_ETD_START_DATE] || null;
  const selectedETDEndDate = values[TICKET_ETD_END_DATE] || null;
  const selectedTask = values.task_name || null;
  const selectedSortBy = values.sort_by || null;
  const selectedCreateStartDate = values[TICKET_CREATE_START_DATE] || null;
  const selectedCreateEndDate = values[TICKET_CREATE_END_DATE] || null;
  const selectedResolvedStartDate = values[TICKET_RESOLVED_START_DATE] || null;
  const selectedResolvedEndDate = values[TICKET_RESOLVED_END_DATE] || null;

  const {
    close: closeCreateTicketModal,
    isOpen: isCreateTicketModalOpen,
    open: openCreateTicketModal,
  } = useModal();

  const onRowClick = (e: any, uuid: string) => {
    e.stopPropagation();
    updateFilter("ticketUuid", uuid);
    setTicketNotes([]);
    setTicketNotesPage(1);
    setOpenSlider(true);
  };

  const [selectedSort, setSelectedSort] = useState<string>(
    selectedSortBy ||
      (resolvedStatus
        ? TICKET_SORT_OPTIONS.TICKET_TYPE_RESOLVED_LATEST_SORT.value
        : TICKET_SORT_OPTIONS.TICKET_TYPE_NEAREST_ETD_SORT.value)
  );

  const isUnassignedAgentSelected = selectedAgent
    ?.split(",")
    .some((v) => v === "unassigned");

  const params = {
    page_num: pageNumber,
    assignee_profile_ids: selectedAssignee || null,
    agent_ids:
      selectedAgent
        ?.split(",")
        .filter((v) => v !== "unassigned")
        .join() || null,
    created_by_profile_ids: selectedCreatedBy || null,
    company_group_uuids: companyGroupUUID,
    resolved_status: resolvedStatus,
    search_term: searchValue,
    tag_filter: selectedTag || null,
    type_filters: selectedTicketType || null,
    etd_filters: selectedTicketETD || null,
    sort_by:
      selectedSort ||
      (resolvedStatus
        ? TICKET_SORT_OPTIONS.TICKET_TYPE_RESOLVED_LATEST_SORT.value
        : TICKET_SORT_OPTIONS.TICKET_TYPE_LATEST_SORT.value),
    priority: selectedPriority || null,
    etd_start_date: selectedETDStartDate,
    etd_end_date: selectedETDEndDate,
    task_name: selectedTask,
    created_start_date: selectedCreateStartDate,
    created_end_date: selectedCreateEndDate,
    resolved_start_date: selectedResolvedStartDate,
    resolved_end_date: selectedResolvedEndDate,
    unassigned_spoc_tickets: isUnassignedAgentSelected || null,
    is_internal_ticket: false,
  };

  const internalTicketsParams = {
    ...params,
    page_num: 1,
    is_internal_ticket: true,
  };

  const [downloadingReport, setDownloadingReport] = useState(false);

  const [downloadReport] = useLazyGetChatTicketReportsQuery();

  const { successToast, alertToast } = useToast();

  const {
    data,
    isLoading: isChatTicketsLoading,
    isFetching: isChatTicketsFetching,
    isSuccess,
  } = useGetChatTicketsQuery(params);

  const { data: internalTicketsData, isLoading: isInternalTicketsLoading } =
    useGetChatTicketsQuery(internalTicketsParams, {
      skip: !canManageInternalTickets || !isInsideGroup,
    });

  const onDownloadReport = async () => {
    setDownloadingReport(true);
    try {
      const downloadUrl = await downloadReport({
        ...params,
        action: "EXPORT_TICKETS",
      }).unwrap();

      openDownloadUrl(downloadUrl);
      successToast({ message: "Tickets report is downloading" });
    } catch (error: any) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
    setDownloadingReport(false);
  };

  const tickets = useMemo(() => {
    const internalTickets = internalTicketsData?.tickets ?? EMPTY_TICKETS;
    const externalTickets = data?.tickets ?? EMPTY_TICKETS;

    if (internalTickets.length === 0) return externalTickets;
    if (externalTickets.length === 0) return internalTickets;

    return [...internalTickets, ...externalTickets];
  }, [internalTicketsData?.tickets, data?.tickets]);

  const { data: assignees = EMPTY_ASSIGNEES } = useGetServiceTeamQuery(
    {
      accessible_teams: canManageInternalTickets
        ? INKLE_TEAM_AND_CPA_TEAM
        : null,
    },
    {
      skip: isCustomer,
    }
  );

  const onSortTabSelect = (sortBy: string) => {
    setSelectedSort(sortBy);
    updateFilter("sort_by", sortBy);
  };

  const { data: tags = EMPTY_TAGS } = useGetTagsForCategoryQuery({
    tagType: "TICKETS_TAG",
  });

  useEffect(() => {
    if (!isChatTicketsFetching) {
      setIsTabSwitched(false);
    }
  }, [isTabSwitched, isChatTicketsFetching]);

  const onSearchQueryChange = debounce((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchValue(value.toLowerCase() || "");
  });

  const tableMeta = useMemo(
    () => ({
      renderTicketTypeIcon,
      resolvedStatus,
      tags,
      callAgents,
      assignees,
      showInternalTicketBadge: canManageInternalTickets,
    }),
    [resolvedStatus, tags, callAgents, assignees, canManageInternalTickets]
  );

  const table = useReactTable({
    data: tickets,
    columns: ticketColumns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
    state: {
      columnPinning: {
        left: ["title"],
      },
    },
    initialState: {
      columnVisibility: {
        tags: true,
        resolved_at: false,
        created_on: true,
        assignee: true,
        title: true,
      },
    },
    meta: tableMeta,
  });

  const isAnyFilterActive =
    !!selectedTag ||
    !!selectedAssignee ||
    !!selectedAgent ||
    !!selectedCreatedBy ||
    !!selectedTicketType ||
    !!selectedTicketETD ||
    !!selectedPriority ||
    !!selectedETDStartDate ||
    !!selectedETDEndDate ||
    !!selectedTask ||
    !!selectedCreateStartDate ||
    !!selectedCreateEndDate ||
    !!selectedResolvedStartDate ||
    !!selectedResolvedEndDate;

  const resetFilter = () => {
    updateMultipleFilters({
      tag_filter: null,
      assignee_profile_ids: null,
      agent_ids: null,
      created_by_profile_ids: null,
      ticket_type: null,
      ticket_etd: null,
      priority: null,
      [TICKET_ETD_START_DATE]: null,
      [TICKET_ETD_END_DATE]: null,
      [TICKET_ETD_SELECT_PERIOD]: null,
      task_name: null,
      [TICKET_CREATE_START_DATE]: null,
      [TICKET_CREATE_END_DATE]: null,
      [TICKET_CREATE_SELECT_PERIOD]: null,
      [TICKET_RESOLVED_START_DATE]: null,
      [TICKET_RESOLVED_END_DATE]: null,
      [TICKET_RESOLVED_SELECT_PERIOD]: null,
      sort_by: null,
      page: 1,
    });
  };

  const onPageNumChange = (page: number) => {
    updateFilter("page", page);
  };

  if (!chatClient) {
    return <Loader />;
  }

  return (
    <div className="t-h-full">
      <div className="t-relative t-top-0 t-h-full t-flex t-flex-col t-pb-4 t-px-5">
        <div className="t-sticky t-top-0 t-bg-white t-z-header t-pb-2">
          <Header
            v2
            title="Support tickets"
            right={
              <Button
                customType="primary"
                onClick={openCreateTicketModal}
                size="xsmall"
              >
                Add Ticket
              </Button>
            }
            className="!t-px-0 !t-pt-0"
          />

          <div className="t-gap-x-6">
            <Tab.Root value="MATCH">
              <Tab.List>
                <Tab.Trigger
                  value={!resolvedStatus ? "MATCH" : "UNMATCH"}
                  onClick={() => {
                    setResolvedStatus(false);
                    setIsTabSwitched(true);
                    table.setColumnVisibility({
                      resolved_at: false,
                    });
                    setSelectedSort(
                      TICKET_SORT_OPTIONS.TICKET_TYPE_LATEST_SORT.value
                    );
                  }}
                >
                  Unresolved
                </Tab.Trigger>
                <Tab.Trigger
                  value={resolvedStatus ? "MATCH" : "UNMATCH"}
                  onClick={() => {
                    setResolvedStatus(true);
                    setIsTabSwitched(true);
                    table.setColumnVisibility({
                      resolved_at: true,
                    });
                    setSelectedSort(
                      TICKET_SORT_OPTIONS.TICKET_TYPE_RESOLVED_LATEST_SORT.value
                    );
                  }}
                >
                  Resolved
                </Tab.Trigger>
              </Tab.List>
            </Tab.Root>
          </div>

          <div className="t-flex t-justify-between t-mt-3">
            <Search
              className="t-w-[440px]"
              onChange={onSearchQueryChange}
              placeholder="Search"
            />

            <Button
              onClick={onDownloadReport}
              customType="icon"
              size="xsmall"
              isLoading={downloadingReport}
              disabled={downloadingReport}
            >
              <span className="t-text-text-30">
                <DownloadIcon color="currentColor" size={20} />
              </span>
            </Button>
          </div>
          <div className="t-mt-3 t-flex t-items-center t-gap-2 t-relative t-justify-between t-bg-white">
            <TicketFilter
              onSortTabSelect={onSortTabSelect}
              selectedTicketETD={selectedTicketETD}
              selectedSort={selectedSort}
              resolvedStatus={resolvedStatus}
            />
            <div className="t-flex t-justify-between t-items-center t-gap-4">
              {isAnyFilterActive && (
                <Button size="xsmall" onClick={resetFilter}>
                  Clear all filters
                </Button>
              )}
              {data && (
                <PaginationHandler
                  page_num={data.current_page}
                  per_page={data.per_page}
                  total_count={data.total_count}
                  total_pages={data.total_pages}
                  isLoading={isChatTicketsFetching && !isChatTicketsLoading}
                  onPageNumChange={onPageNumChange}
                />
              )}
            </div>
          </div>
        </div>

        <div className="t-w-full t-flex-1 t-mt-6 t-overflow-x-auto t-bg-white">
          <Table.Container
            sticky
            className="t-min-w-[120rem] t-max-w-full t-h-full"
          >
            <Table.Content>
              <Table.Head>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.Row key={headerGroup.id} className="t-z-[11]">
                    {headerGroup?.headers?.map((header, idx) => {
                      const styles = getCommonPinningStyles(header.column);
                      return (
                        <Table.HeadCell
                          key={header.id}
                          style={{
                            ...styles,
                            width: `${header.getSize()}%`,
                            zIndex: idx === 0 ? 20 : undefined,
                          }}
                          className={classNames({
                            "t-sticky t-left-0 t-z-20 t-bg-white t-py-3 t-px-1.5 t-drop-shadow-table-left-pin":
                              idx === 0,
                          })}
                        >
                          <span className="t-flex t-items-center t-gap-1">
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            {idx < headerGroup?.headers.length - 2 && (
                              <Table.HeadCellSeparator />
                            )}
                          </span>
                        </Table.HeadCell>
                      );
                    })}
                  </Table.Row>
                ))}
              </Table.Head>
              <Async.Root
                {...{
                  isLoading: isChatTicketsLoading || isInternalTicketsLoading,
                  isSuccess,
                  isEmpty: tickets.length === 0,
                  customLoader: (
                    <div className="t-fixed t-inset-0 t-flex t-justify-center t-items-center t-mt-40">
                      <Loader />
                    </div>
                  ),
                }}
              >
                <Async.Empty>
                  <div className="t-fixed t-ml-[400px] t-mt-[200px]">
                    {isAnyFilterActive ? (
                      <div className="t-flex t-flex-col t-justify-center t-items-center t-w-[400px]">
                        <Wind className="t-text-neutral-40" />
                        <div className="t-text-subtext t-text-text-60 t-mt-4">
                          No tickets found
                        </div>
                        <div className="t-text-body-sm t-text-text-30 t-mb-6">
                          Try adjusting or clearing your filters to see more
                          results
                        </div>
                        <Button size="small" onClick={resetFilter}>
                          Clear all filters
                        </Button>
                      </div>
                    ) : (
                      <div className="t-flex t-flex-col t-justify-center t-items-center t-w-[400px]">
                        <TableEmptyState
                          title="No tickets found"
                          icon={<EmptyList />}
                        />
                      </div>
                    )}
                  </div>
                </Async.Empty>
                <Async.Success>
                  <Table.Body>
                    {table.getRowModel().rows.map((row) => {
                      const {
                        original: {
                          uuid,
                          is_etd_overdue: isEtdOverdue,
                          is_etd_upcoming: isEtdDueSoon,
                        },
                      } = row;

                      return (
                        <Table.Row
                          key={row.id}
                          className={classNames(
                            "t-group hover:t-bg-surface-lighter-grey t-cursor-pointer",
                            {
                              "t-bg-red-10": isEtdOverdue && !resolvedStatus,
                              "t-bg-yellow-10": isEtdDueSoon && !resolvedStatus,
                            }
                          )}
                          onClick={(e) => onRowClick(e, uuid)}
                        >
                          {row.getVisibleCells().map((cell, idx) => {
                            const styles = getCommonPinningStyles(cell.column);
                            return (
                              <Table.Cell
                                key={cell.id}
                                style={{
                                  ...styles,
                                  width: `${cell.column.getSize()}%`,
                                  zIndex: idx === 0 ? 5 : undefined,
                                }}
                                className={classNames({
                                  "group-hover:!t-bg-surface-lighter-grey t-z-[5] t-drop-shadow-table-left-pin":
                                    idx === 0,
                                  "!t-bg-red-10":
                                    isEtdOverdue &&
                                    idx === 0 &&
                                    !resolvedStatus,
                                  "!t-bg-yellow-10":
                                    isEtdDueSoon &&
                                    idx === 0 &&
                                    !resolvedStatus,
                                })}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </Table.Cell>
                            );
                          })}
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Async.Success>
              </Async.Root>
            </Table.Content>
          </Table.Container>

          {openSlider && (
            <TicketSlider
              resolvedStatus={resolvedStatus}
              openSlider={openSlider}
              setOpenSlider={setOpenSlider}
              ticketNotes={ticketNotes}
              setTicketNotes={setTicketNotes}
              ticketNotesPage={ticketNotesPage}
              setTicketNotesPage={setTicketNotesPage}
              chatClient={chatClient}
              isInsideGroup={isInsideGroup}
            />
          )}
        </div>
      </div>
      {isCreateTicketModalOpen && (
        <CreateTicket
          isNonChatTicket={true}
          show={isCreateTicketModalOpen}
          closeModal={closeCreateTicketModal}
        />
      )}
    </div>
  );
};
